import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { omit } from 'lodash-es';
import { AdminPageHeader, DeleteModal, Form, AdminFormAction } from '@ui';
import { DEFAULT_HUB_VALUES, useHubFormSchema } from '@lp/features/admin/forms/HubForm/config';
import { NewHubForm } from '@lp/features/admin/forms/HubForm/types';
import { HubForm } from '@lp/features/admin/forms/HubForm/components/HubForm';
import { AuthContext } from '@lp/core/auth';
import { Hub } from '@/api/apis/facilities';
import { useUpdateHubMutation } from '@/queries/hub/hub.mutation';
import { useLoaderData } from '@/hooks/useLoaderData';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { convertToIanaTimezone, getChangedValues, getShortTzByIanaName } from '@/utils/helpers';
import { retrieveUnifiedError } from '@/api/utils';
import { useWrappedRemoveHubMutation } from '@/queries/orchestrator';
import { AdminPermissions } from '@/permissions';

export const HubEdit = () => {
  const navigate = useNavigate();
  const data = useLoaderData<Hub>();
  const { mutateAsync } = useUpdateHubMutation();
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.NopHubCreate);
  const hasRemovePermissions = checkPermission(AdminPermissions.NopHubRemove);
  const { schema, isLoading: isGeoDataLoading } = useHubFormSchema();

  const defaultValues = useMemo<NewHubForm>(() => {
    return {
      ...DEFAULT_HUB_VALUES,
      name: data.name,
      code: data.code,
      isSuperHub: data.isSuperHub,
      timezone: getShortTzByIanaName(data.location.timezone) || '',
      timezoneIANA: data.location.timezone || '',
      region: data.location.region,
      regionId: data.location.regionId,
      addressLine: data.location.addressLine,
      country: data.location.country,
      city: data.location.city,
      state: data.location.state,
      latitude: data.location.latitude,
      longitude: data.location.longitude,
      postalCode: data.location.postalCode,
      nationalManagerId: data.nationalManagerId || '',
      nationalManagerName: data.nationalManager
        ? `${data.nationalManager?.firstName} ${data.nationalManager?.lastName}`
        : '',
      regionalManagerId: data.regionalManagerId || '',
      regionalManagerName: data.regionalManagerId
        ? `${data.regionalManager?.firstName} ${data.regionalManager?.lastName}`
        : '',
    };
  }, [data]);

  const handleBackToList = () => {
    navigate('/admin/hubs/locations');
  };

  const handleSubmit = async (values: NewHubForm) => {
    try {
      const updateValues = {
        ...omit(values, ['regionalManagerName', 'nationalManagerName', 'addressId', 'timezone']),
        nationalManagerId: values.nationalManagerId || null,
        regionalManagerId: values.regionalManagerId || null,
        timezone: values.timezoneIANA || convertToIanaTimezone(values.timezone),
      };
      const changedValues = getChangedValues<Partial<NewHubForm>>(updateValues, defaultValues);

      await mutateAsync({
        id: data.id,
        ...changedValues,
      });

      toast.success(`Hub updated successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useWrappedRemoveHubMutation,
    successMessage: 'Hub deleted successfully.',
    onDeleteCb: handleBackToList,
  });
  const handleHubDelete = () => {
    onModalOpen(data.id);
  };

  if (isGeoDataLoading) return null;

  return (
    <>
      <Form<NewHubForm> defaultValues={defaultValues} schema={schema} onSubmit={handleSubmit}>
        <div className="flex flex-col gap-9">
          <AdminPageHeader title="Edit NOP Hub" backToListLink="/admin/hubs/locations">
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              handleDeleteClick={handleHubDelete}
              hasEditPermissions={hasCreatePermissions}
              hasDeletePermissions={hasRemovePermissions}
            />
          </AdminPageHeader>
          <HubForm isReadOnly={!hasCreatePermissions} />
        </div>
      </Form>
      <DeleteModal
        title="Delete NOP Hub?"
        description="Are you sure you want to delete the NOP Hub?"
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
